import { defineStore } from 'pinia'

import type {
	IFetchApiCreateProduct,
	IFetchApiDeleteDbProduct,
	IFetchApiExportProduct,
	IFetchApiExportProductTemplate,
	IFetchApiFindProduct,
	IFetchApiGetManufacturerList,
	IFetchApiProductAttributeList,
	IFetchApiProductFilters,
	IFetchApiProductList,
	IFetchApiProductTypeList,
	IFetchApiUpdateDBProduct,
	IFetchApiUpdateProjectProduct
} from '~/portal/api'
import {
	fetchApiCreateProduct,
	fetchApiDeleteDBProduct,
	fetchApiExportProduct,
	fetchApiExportProductTemplate,
	fetchApiFindProduct,
	fetchApiGetManufacturerList,
	fetchApiProductAttributeList,
	fetchApiProductFilters,
	fetchApiProductList,
	fetchApiProductTypeList,
	fetchApiUpdateDBProduct,
	fetchApiUpdateProjectProduct
} from '~/portal/api'
import { ICPProductAdditionalAttrs } from '~/portal/components'
import type { IProductsStoreTypes } from '~/portal/stores'

export const useProductsStore = defineStore('useProductsStore', {
	state: (): IProductsStoreTypes => ({
		productList: [],
		attrsList: [],
		productTypeList: [],
		manufacturerList: [],
		totalCount: 0,
		productFilters: null
	}),
	getters: {
		getAttrsList(state: IProductsStoreTypes) {
			return [
				{
					id: 0,
					name: ICPProductAdditionalAttrs.Type,
					formula_symbol: ''
				},
				{
					id: 1,
					name: ICPProductAdditionalAttrs.Manufacturer,
					formula_symbol: ''
				},
				{
					id: 2,
					name: ICPProductAdditionalAttrs.CertificateNumber,
					formula_symbol: ''
				},
				...state.attrsList
			]
		}
	},
	actions: {
		resetProductState() {
			this.productList = []
			this.attrsList = []
		},
		async fetchProductFilters(payload: IFetchApiProductFilters) {
			const resp = await fetchApiProductFilters(payload)

			this.productFilters = resp?.data?.types

			return this.productFilters
		},
		async fetchProductList(payload: IFetchApiProductList) {
			const response = await fetchApiProductList(payload)
			if (!response?.data) return

			let data: any[], total: number

			if (payload.params?.return_all) {
				data = response.data
				total = response.data.length
			} else {
				data = response.data.results
				total = response.data.count
			}

			this.totalCount = total

			const pageNum = payload.params?.page ? +payload.params.page : 1
			if (pageNum > 1) {
				this.productList = [...this.productList, ...data]
			} else {
				this.productList = data
			}
		},
		async fetchProductAttributeList(payload: IFetchApiProductAttributeList) {
			const response = await fetchApiProductAttributeList(payload)

			if (response && response.data) {
				this.attrsList = response.data
			}
		},
		async fetchProductTypeList(payload: IFetchApiProductTypeList) {
			const response = await fetchApiProductTypeList(payload)

			if (response && response.data) {
				this.productTypeList = response.data
			}
		},
		async fetchManufacturerList(payload: IFetchApiGetManufacturerList) {
			const response = await fetchApiGetManufacturerList(payload)

			if (response) {
				this.manufacturerList = response
			}
		},
		async deleteDbProduct(payload: IFetchApiDeleteDbProduct) {
			return await fetchApiDeleteDBProduct(payload)
		},
		async createProduct(payload: IFetchApiCreateProduct) {
			return await fetchApiCreateProduct(payload)
		},
		async updateProduct(payload: IFetchApiUpdateDBProduct) {
			return await fetchApiUpdateDBProduct(payload)
		},
		async findProduct(payload: IFetchApiFindProduct) {
			return await fetchApiFindProduct(payload)
		},
		async exportDbProduct(payload: IFetchApiExportProduct) {
			return await fetchApiExportProduct(payload)
		},
		async exportProductTemplate(payload: IFetchApiExportProductTemplate) {
			return await fetchApiExportProductTemplate(payload)
		},
		async updateProjectProduct(payload: IFetchApiUpdateProjectProduct) {
			const response = await fetchApiUpdateProjectProduct(payload)

			if (response) {
				const product = this.productList.find(item => item.id === payload.id)

				if (product) {
					product.amount = payload.payload.amount
				}
			}
		}
	}
})
